// 흐르는 시각을 화면의 칸에 맞춘다
//
// ── 왜 이 파일이 필요한가 ───────────────────────────────────────────────────
//
// 타이밍 코어의 표시 형식기는 **0.1초의 배수가 아닌 값을 거절한다.** 그 방어는 옳다 —
// 거기 오는 값은 사람이 적어 넣은 것이거나 그것에서 계산된 것이고, 0.1초보다 잔 자리가
// 섞였다는 것은 어딘가에서 단위가 틀어졌다는 뜻이기 때문이다.
//
// 그런데 카운트다운은 사정이 다르다. `개시 − 지금` 은 매 프레임 임의의 밀리초라, 그대로
// 넘기면 형식기가 예외를 던지고 **화면이 통째로 사라진다.** 실제로 그랬다 — 열람 화면을
// 처음 띄웠을 때 React 가 렌더 도중 던져진 예외로 트리를 걷어내 빈 화면이 남았고, 표식이
// 하나도 없는 그 상태를 검사는 「화면이 없다」로만 보고했다. 앞의 다섯 스텝은 흐르는 시계를
// 그린 적이 없어 이 자리를 만나지 못했다. (2026-08-14.)
//
// 그래서 **화면이 자기 칸에 맞춰 자른다.** 코어의 방어를 무르게 하지 않고, 자르는 규칙을
// 화면 층이 눈에 보이게 지는 편이 낫다.
//
// ── 왜 버림인가 (반올림도 올림도 아니고) ────────────────────────────────────
//
// 0.1초 칸으로 자르면 표시가 실제와 최대 100밀리초 어긋나고, **어느 쪽으로 어긋나느냐가
// 안전을 가른다.**
//
//   올림    4.91초 남았는데 `0:05.0` 이라 적는다 — 있는 것보다 **많은** 시간을 약속한다.
//           그 약속을 믿은 사람은 늦게 누른다.
//   버림    4.99초 남았는데 `0:04.9` 라 적는다 — 있는 것보다 **적은** 시간을 말한다.
//           그 말을 믿은 사람은 조금 이르게 준비한다.
//
// 이 제품에서 늦음은 곧 rally 실패다. 그래서 **남은 시간을 과장하지 않는 쪽**으로 자른다.
//
// 음수 쪽에서도 같은 규칙이 그대로 성립한다. −0.437초는 `−0:00.5` 가 되어 지나간 폭을
// 과장하는데, 그것 역시 「늦었다는 사실을 축소하지 않는다」는 같은 방향이다.
//
// **소리는 이 자르기에 걸리지 않는다.** 신호 예약은 오디오 시계 위의 실수 초를 쓰고 이
// 함수를 지나지 않는다. 화면의 숫자와 귀의 박자가 서로 다른 층인 것이 계약이다 — 소리는
// 박자와 종점을 나르고, 남은 초의 **위치**는 화면이 나른다.

import { duration } from '../../../core/time/brand.ts'
import { formatCountdown, formatDuration } from '../../../core/timing/format.ts'

/** 화면이 쓰는 시간 칸. 정본이 정한 표시 해상도이고 여기서 정하는 값이 아니다. */
const DISPLAY_STEP_MS = 100

/**
 * 남은 밀리초를 `T−m:ss.t` 로 적는다. **과장하지 않는 쪽으로 자른다.**
 *
 * `Math.floor` 는 음수에서도 아래로 가므로 부호마다 규칙을 가르지 않아도 된다 — 가르면
 * 0 근처에서 표시가 한 칸 튀고, 그 튐이 하필 사람이 누르는 순간에 일어난다.
 */
export function formatRemaining(millis: number): string {
  return formatCountdown(duration(Math.floor(millis / DISPLAY_STEP_MS) * DISPLAY_STEP_MS))
}

/**
 * 개시를 **지난 뒤** 얼마나 지났는가. `+11:33.4` 꼴이다.
 *
 * ── 왜 따로 필요한가 ──────────────────────────────────────────────────────
 *
 * `formatCountdown` 은 앞에 `T−` 를 붙이는데 그 뒤에 음수가 오면 `T−-11:33.4` 가 된다.
 * 빼기가 둘이라 오타로 읽히고, 더 나쁜 것은 **라벨이 「개시까지」로 남는다**는 점이다 —
 * 11분 전에 지나간 순간을 화면이 「앞으로 올 것」이라고 말한다. 지나갔다는 사실을 감추지
 * 않는다는 원칙은 옳지만, 그 원칙을 지키는 방법이 **음수를 그대로 흘리는 것**이어서는
 * 안 된다. (2026-08-14 대리 관측이 잡았다.)
 *
 * 부호를 `+` 로 두는 것은 이 저장소가 이미 쓰는 문법이다 — 지나간 폭을 양수로 읽는다.
 * 자르는 방향은 남은 시간과 같은 버림이고, 여기서는 **지난 폭을 부풀리지 않는** 쪽이다.
 */
export function formatSinceOpen(millis: number): string {
  return `+${formatDuration(duration(Math.floor(millis / DISPLAY_STEP_MS) * DISPLAY_STEP_MS))}`
}
